import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Satellite, RefreshCw, Check, X, Plus, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RadarAdminPanel = () => {
  const [items, setItems] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [jobForm, setJobForm] = useState({ title: "", city: "", source_url: "" });
  const [kindFilter, setKindFilter] = useState("all");

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/radar/items?status=pending`);
      setItems(res.data.items || []);
      setLastRun(res.data.last_run);
    } catch {
      toast.error("Erro ao carregar itens do Radar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/radar/run`);
      toast.success(res.data.message);
      setTimeout(fetchItems, 60000);
      setTimeout(fetchItems, 20000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao disparar o Radar");
    } finally {
      setRunning(false);
    }
  };

  const handleAction = async (id, action) => {
    try {
      const res = await axios.post(`${API}/admin/radar/items/${id}/${action}`);
      if (action === "approve") toast.success(res.data.message);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const handleAddJob = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/radar/external-job`, jobForm);
      toast.success(res.data.message);
      setJobForm({ title: "", city: "", source_url: "" });
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Erro ao publicar vaga");
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground py-4">Carregando Radar...</p>;

  return (
    <div className="space-y-6" data-testid="radar-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Satellite className="w-5 h-5" /> Radar HeveaTalent
        </h2>
        <Button onClick={handleRun} disabled={running} data-testid="radar-run-btn" className="bg-green-700 hover:bg-green-800">
          <RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />
          Buscar agora
        </Button>
      </div>

      {lastRun && (
        <p className="text-xs text-muted-foreground" data-testid="radar-last-run">
          Última busca: {new Date(lastRun.finished_at).toLocaleString("pt-BR")} · Dólar: {lastRun.dolar ? `R$ ${lastRun.dolar}` : "—"} · Notícias novas: {lastRun.news_added} · Vagas novas: {lastRun.jobs_added} · Descartadas pela IA: {lastRun.descartadas}
        </p>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Fila de Aprovação ({items.length})</CardTitle>
          <div className="flex flex-wrap gap-1 mt-2" data-testid="radar-kind-filters">
            {[
              { key: "all", label: `Todas (${items.length})` },
              { key: "quotation", label: `💰 Cotações (${items.filter((i) => i.kind === "quotation").length})` },
              { key: "news", label: `📰 Notícias (${items.filter((i) => i.kind === "news").length})` },
              { key: "job", label: `👷 Vagas (${items.filter((i) => i.kind === "job").length})` },
              { key: "course", label: `🎓 Cursos (${items.filter((i) => i.kind === "course").length})` },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setKindFilter(f.key)}
                data-testid={`radar-filter-${f.key}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  kindFilter === f.key ? "bg-green-700 text-white border-green-700" : "bg-white text-gray-600 border-gray-300 hover:border-green-500"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {items.filter((i) => kindFilter === "all" || i.kind === kindFilter).length === 0 ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="radar-empty-queue">
              {items.length === 0
                ? 'Nenhum item aguardando. Clique em "Buscar agora" ou aguarde a busca automática (seg-sex, 6h).'
                : "Nenhum item desse tipo na fila."}
            </p>
          ) : (
            <div className="space-y-3">
              {items.filter((i) => kindFilter === "all" || i.kind === kindFilter).map((item) => (
                <div key={item.id} className="border rounded-lg p-3" data-testid={`radar-item-${item.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={item.kind === "news" ? "bg-blue-100 text-blue-800" : item.kind === "quotation" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
                          {item.kind === "news" ? "📰 Notícia" : item.kind === "quotation" ? "💰 Cotação" : "👷 Vaga"}
                        </Badge>
                        {item.pub_date && !isNaN(new Date(item.pub_date).getTime()) && (
                          <span className="text-[11px] text-gray-400" data-testid={`radar-item-date-${item.id}`}>
                            📅 {new Date(item.pub_date).toLocaleDateString("pt-BR")}
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-sm mt-1">{item.title}</p>
                      {item.summary && <p className="text-xs text-gray-600 mt-1">{item.summary}</p>}
                      <a href={item.source_url} target="_blank" rel="noreferrer" className="text-xs text-green-700 hover:underline inline-flex items-center gap-1 mt-1">
                        <ExternalLink className="w-3 h-3" /> {item.source_name || "Ver fonte"}
                      </a>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto sm:shrink-0">
                      <Button size="sm" className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700" data-testid={`radar-approve-${item.id}`} onClick={() => handleAction(item.id, "approve")}>
                        <Check className="w-4 h-4 sm:mr-0" /><span className="sm:hidden ml-1">Aprovar</span>
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 sm:flex-none text-red-600 border-red-300" data-testid={`radar-reject-${item.id}`} onClick={() => handleAction(item.id, "reject")}>
                        <X className="w-4 h-4 sm:mr-0" /><span className="sm:hidden ml-1">Descartar</span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-4 h-4" /> Adicionar vaga externa manualmente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddJob} className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ej-title">Título da vaga *</Label>
              <Input id="ej-title" data-testid="external-job-title" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} placeholder="Ex: Contrata sangrador em Olímpia-SP" required />
            </div>
            <div>
              <Label htmlFor="ej-city">Cidade</Label>
              <Input id="ej-city" data-testid="external-job-city" value={jobForm.city} onChange={(e) => setJobForm({ ...jobForm, city: e.target.value })} placeholder="Ex: Olímpia - SP" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ej-url">Link da fonte (site, grupo, anúncio) *</Label>
              <Input id="ej-url" data-testid="external-job-url" type="url" value={jobForm.source_url} onChange={(e) => setJobForm({ ...jobForm, source_url: e.target.value })} placeholder="https://..." required />
            </div>
            <Button type="submit" data-testid="external-job-submit" className="bg-green-700 hover:bg-green-800 sm:col-span-2">
              Publicar vaga externa no Balcão
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RadarAdminPanel;
